import { Box, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useMemo, useRef, useState } from 'react';
import { chartPanelSelectMenuProps, chartPanelSelectSx, perf10kDropdownsGridSx } from '../styles/chartReader.styles';
import { FindDataPointSectionTitleTypography, HintTypography, LabelTypography } from '../styles/chartReader.typography';
import { formatLongDate, monthName, parseIsoDate } from '../utils/perf10kFormat';
import { focusSelectTrigger } from '../utils/selectFocus';
import { ChartPanelSubmitButton } from './ChartPanelSubmitButton';

/** Minimum contract every chart-type data point must satisfy to drive the selector. */
export type DateBearingPoint = { date: string };

/** Screen-reader guidance for `ChartDataSectionShell`'s Submit-triggered
 *  announcement — accurate only because Tab from Submit loops back to Year
 *  (see `handleTabAwayFromSubmit` below) rather than leaving the panel. Kept
 *  next to that behavior so the two can't drift apart silently. */
export const DATE_POINT_SUBMIT_GUIDANCE = 'Press Tab to select a different date.';

type DataPointSelectorProps<T extends DateBearingPoint> = {
    /** All points the user can choose from. Order is irrelevant — the selector
     *  derives Year/Month/Date options by parsing `date`. */
    points: T[];
    /** Prefix for the Year/Month/Date `<select>` IDs so multiple instances on a
     *  page (or future chart types) never collide. E.g. `"perf10k"`, `"shout_tna"`. */
    idPrefix: string;
    /** Called only when the user presses Submit after picking a Date. Year/Month/
     *  Date selections themselves never fire this callback, so the parent's
     *  summary — and the shell's NVDA re-announcement — stays frozen at the last
     *  confirmed point until Submit is pressed (WCAG 2.2 3.2.2 On Input). */
    onSelectedPointChange: (point: T) => void;
};

/** Pair every point with its parsed parts once — used by all three dropdowns. */
function parsePoints<T extends DateBearingPoint>(points: T[]) {
    const parsed: Array<{ point: T; year: number; month: number; day: number }> = [];
    for (const point of points) {
        const parts = parseIsoDate(point.date);
        if (parts) parsed.push({ point, ...parts });
    }
    return parsed;
}

/**
 * Chart-type-agnostic Year/Month/Date dropdown selector. Any chart whose data
 * points carry a `date: string` ('YYYY-MM-DD') can drop this in — the selector
 * itself never touches the rest of the point shape, it just hands the picked
 * point back to the parent via `onSelectedPointChange`.
 */
export function DataPointSelector<T extends DateBearingPoint>({
    points,
    idPrefix,
    onSelectedPointChange,
}: DataPointSelectorProps<T>) {
    const [year, setYear] = useState<number | ''>('');
    const [month, setMonth] = useState<number | ''>('');
    const [isoDate, setIsoDate] = useState<string>('');

    const yearControlRef = useRef<HTMLDivElement>(null);
    const monthControlRef = useRef<HTMLDivElement>(null);
    const dateControlRef = useRef<HTMLDivElement>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);

    const parsedPoints = useMemo(() => parsePoints(points), [points]);

    const yearOptions = useMemo(() => {
        const years = new Set<number>();
        for (const p of parsedPoints) years.add(p.year);
        // Descending — most recent years first, since users typically reach for
        // the latest data point.
        return Array.from(years).sort((a, b) => b - a);
    }, [parsedPoints]);

    const monthOptions = useMemo(() => {
        if (year === '') return [];
        const months = new Set<number>();
        for (const p of parsedPoints) {
            if (p.year === year) months.add(p.month);
        }
        return Array.from(months).sort((a, b) => a - b);
    }, [parsedPoints, year]);

    /** [{ point, day }] for the selected year+month, sorted by day ascending. */
    const dateOptions = useMemo(() => {
        if (year === '' || month === '') return [];
        return parsedPoints
            .filter((p) => p.year === year && p.month === month)
            .sort((a, b) => a.day - b.day)
            .map((p) => ({ point: p.point, day: p.day }));
    }, [parsedPoints, year, month]);

    const handleYearChange = useCallback((event: SelectChangeEvent<number | ''>) => {
        const next = event.target.value as number;
        setYear(next);
        setMonth('');
        setIsoDate('');
        // Intentionally NOT calling onSelectedPointChange here — the parent's
        // last confirmed selection stays in effect so NVDA doesn't re-announce
        // the default summary every time the user starts a fresh date pick.
        // Wait for React to flush the disabled→enabled state change on Month
        // before focusing — disabled triggers can't receive focus.
        setTimeout(() => focusSelectTrigger(monthControlRef), 50);
    }, []);

    const handleMonthChange = useCallback((event: SelectChangeEvent<number | ''>) => {
        const next = event.target.value as number;
        setMonth(next);
        setIsoDate('');
        // See handleYearChange — no callback fire on intermediate Month pick.
        setTimeout(() => focusSelectTrigger(dateControlRef), 50);
    }, []);

    const handleDateChange = useCallback((event: SelectChangeEvent<string>) => {
        // Only updates local state — the parent callback fires from the Submit
        // button below so the summary (and NVDA re-announcement) doesn't change
        // until the user explicitly confirms the pick (WCAG 2.2 3.2.2 On Input).
        setIsoDate(event.target.value);
        // Date is the last field in the sequence, so completing it moves focus
        // straight to Submit — same "wait for the disabled→enabled flip" delay
        // as the Year/Month advances above (Submit is disabled until this
        // state update lands).
        setTimeout(() => submitButtonRef.current?.focus(), 50);
    }, []);

    // Submit is the last stop in the selection loop — Tab from here would
    // otherwise leave the panel entirely (e.g. into the page footer), so loop
    // back to Year instead, letting the user immediately start a new pick.
    const handleTabAwayFromSubmit = useCallback(() => {
        focusSelectTrigger(yearControlRef);
    }, []);

    /** Day-of-month for the currently-selected ISO date, for the collapsed Select label. */
    const selectedDay = useMemo(() => {
        if (!isoDate) return null;
        return parseIsoDate(isoDate)?.day ?? null;
    }, [isoDate]);

    /** The point matching the current Date pick, or null until one is made. */
    const pendingPoint = useMemo(
        () => dateOptions.find((d) => d.point.date === isoDate)?.point ?? null,
        [dateOptions, isoDate],
    );

    const handleSubmit = useCallback(() => {
        if (pendingPoint) onSelectedPointChange(pendingPoint);
    }, [pendingPoint, onSelectedPointChange]);

    const submitAriaLabel = pendingPoint
        ? `Submit and display the value for ${formatLongDate(pendingPoint.date)}`
        : 'Submit — select a year, month, and day to continue';

    const isMonthDisabled = year === '' || monthOptions.length === 0;
    const isDateDisabled = month === '' || dateOptions.length === 0;

    const yearId = `${idPrefix}-year-select`;
    const monthId = `${idPrefix}-month-select`;
    const dateId = `${idPrefix}-date-select`;

    return (
        <Box>
            <FindDataPointSectionTitleTypography>Find a specific data point</FindDataPointSectionTitleTypography>
            <HintTypography sx={{ mt: 0.5 }}>
                Select the year, month, and day, then press Submit to display the value for that date.
            </HintTypography>

            <Box sx={perf10kDropdownsGridSx}>
                {/* Year */}
                <Box>
                    <label htmlFor={yearId}>
                        <LabelTypography sx={{ mb: 0.5 }}>Year</LabelTypography>
                    </label>
                    <FormControl ref={yearControlRef} fullWidth size='small' sx={chartPanelSelectSx}>
                        <Select<number | ''>
                            id={yearId}
                            value={year}
                            onChange={handleYearChange}
                            displayEmpty
                            renderValue={(v) => (typeof v === 'number' ? String(v) : 'Select year')}
                            inputProps={{ 'aria-label': year === '' ? 'Select year' : 'Year' }}
                            MenuProps={chartPanelSelectMenuProps}>
                            {yearOptions.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Month */}
                <Box>
                    <label htmlFor={monthId}>
                        <LabelTypography sx={{ mb: 0.5 }}>Month</LabelTypography>
                    </label>
                    <FormControl
                        ref={monthControlRef}
                        fullWidth
                        size='small'
                        disabled={isMonthDisabled}
                        sx={chartPanelSelectSx}>
                        <Select<number | ''>
                            id={monthId}
                            value={month}
                            onChange={handleMonthChange}
                            displayEmpty
                            renderValue={(v) => (typeof v === 'number' ? monthName(v) : 'Select month')}
                            inputProps={{ 'aria-label': month === '' ? 'Select month' : 'Month' }}
                            MenuProps={chartPanelSelectMenuProps}>
                            {monthOptions.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {monthName(m)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Date — day number only */}
                <Box>
                    <label htmlFor={dateId}>
                        <LabelTypography sx={{ mb: 0.5 }}>Date</LabelTypography>
                    </label>
                    <FormControl
                        ref={dateControlRef}
                        fullWidth
                        size='small'
                        disabled={isDateDisabled}
                        sx={chartPanelSelectSx}>
                        <Select
                            id={dateId}
                            value={isoDate}
                            onChange={handleDateChange}
                            displayEmpty
                            renderValue={() => (selectedDay !== null ? String(selectedDay) : 'Select date')}
                            inputProps={{ 'aria-label': isoDate === '' ? 'Select date' : 'Date' }}
                            MenuProps={chartPanelSelectMenuProps}>
                            {dateOptions.map(({ point, day }) => (
                                <MenuItem key={point.date} value={point.date}>
                                    {day}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <ChartPanelSubmitButton
                ref={submitButtonRef}
                disabled={!pendingPoint}
                ariaLabel={submitAriaLabel}
                onSubmit={handleSubmit}
                onTabAway={handleTabAwayFromSubmit}
            />
        </Box>
    );
}
